import { AnyAction } from '@reduxjs/toolkit';
import { IQuestion } from 'types/Question.types';
import {
  COMPANY_CHANGED,
  QUERY_ATTEMPT,
  QUERY_ERROR,
  QUERY_RESET,
  QUERY_SUCCESS,
  RESET_CONFIGURE_FORM,
  SET_MODEL,
  SET_QUESTIONS,
  SET_PRICING,
  SET_MODELS,
  SET_TRIGGER_QUERY_REQUEST_AFTER_FORM_RESET,
  SET_IMAGES,
  SET_LANG,
  SET_QUERY_PAYLOAD,
  SET_IS_USING_SELLER_SYSTEM_DISCOUNTS,
  SET_EXTRA_PRICE_QUESTIONS,
  SET_BOM_ITEMS,
  SET_VARIABLES,
  SET_CUSTOM_NET_PRICE,
  SET_RESET_DISCOUNT_LINES,
  SET_PREDEFINED_PRICING,
} from '../../constants';
import { IConfigurationQuestion } from 'types/ConfigurationQuestion.types';

export interface IPredefinedPricing {
  currency: string;
  exchangeRate: number;
}

// Model variables as they come back from the query, keyed by variable name
// (V_Fabric_width and the like). Their value is whatever the model computed,
// so it is only ever read for display - on SpecsPage.
export type IVariables = Record<string, { value: unknown }>;

export interface IModelReducerState {
  models: any[] | null;
  id: number | null;
  versionNumber: number | null;
  general: object | null;
  images: object | null;
  questions: IQuestion[];
  queryPayload: object | null;
  pricing: object | null;
  roundPricing: boolean;
  bomItems: any | null;
  variables: IVariables | null;
  queryStatus: {
    attempt: boolean;
    success: boolean;
    error: string | null;
  };
  triggerQueryRequestAfterFormReset: boolean;
  lang: string;
  isUsingSupplierSystemDiscounts: boolean;
  extraPriceQuestions: IConfigurationQuestion[];
  connLeadMpr: string | null;
  connLeadTime: number | null;
  customNetPrice?: number | null;
  resetDiscountLines: Set<string>;
  hasBomItems: boolean;
  predefinedPricing: IPredefinedPricing | null;
}

const INIT_STATE: IModelReducerState = {
  models: null,
  id: null,
  versionNumber: null,
  general: null,
  images: null,
  questions: [],
  queryPayload: null,
  pricing: null,
  roundPricing: false,
  bomItems: null,
  variables: null,
  queryStatus: {
    attempt: false,
    success: false,
    error: null,
  },
  triggerQueryRequestAfterFormReset: false, // Used in useValuesDidUpdate
  lang: 'en',
  isUsingSupplierSystemDiscounts: false,
  extraPriceQuestions: [],
  connLeadMpr: null,
  connLeadTime: null,
  resetDiscountLines: new Set<string>(),
  hasBomItems: false,
  predefinedPricing: null,
};

export const parseQuestionsObjectResponseIntoArray = (questionsObj: object) => {
  return Object.keys(questionsObj).map((questionKey: string) => {
    return {
      ...questionsObj[questionKey],
      name: questionsObj[questionKey].name
        ? questionsObj[questionKey].name
        : questionKey,
      initialKey: questionsObj[questionKey].initialKey
        ? questionsObj[questionKey].initialKey
        : questionKey,
    };
  });
};

const modelReducer = (state = INIT_STATE, action: AnyAction) => {
  switch (action.type) {
    case SET_MODEL:
      return {
        ...state,
        id: action.payload.id,
        versionNumber: action.payload.version_number,
        general: action.payload.general,
        roundPricing: action.payload.round_pricing,
        connLeadMpr: action.payload.conn_lead_mpr,
        connLeadTime: action.payload.conn_lead_time,
        bomItems: action.payload.bom_items,
        hasBomItems: action.payload.has_bom_items || false,
      };
    case SET_QUESTIONS: {
      if (!action.payload || typeof action.payload !== 'object') {
        return {
          ...state,
          questions: [],
        };
      }
      // If present, use question.name as name, else use question key as its name
      const questionsArray = parseQuestionsObjectResponseIntoArray(
        action.payload
      );

      return {
        ...state,
        questions: questionsArray,
      };
    }
    case SET_QUERY_PAYLOAD:
      return {
        ...state,
        queryPayload: action.payload,
      };
    case SET_IMAGES:
      return {
        ...state,
        images: action.payload,
      };
    case SET_PRICING:
      return {
        ...state,
        pricing: action.payload,
      };
    case SET_CUSTOM_NET_PRICE:
      return {
        ...state,
        customNetPrice: action.payload,
      };
    case SET_BOM_ITEMS:
      return {
        ...state,
        bomItems: action.payload,
      };
    case SET_VARIABLES:
      return {
        ...state,
        variables: action.payload || null,
      };

    case QUERY_ATTEMPT:
      return {
        ...state,
        queryStatus: {
          attempt: true,
          success: false,
          error: null,
        },
      };
    case QUERY_SUCCESS:
      return {
        ...state,
        queryStatus: {
          attempt: false,
          success: true,
          error: null,
        },
      };
    case QUERY_ERROR:
      return {
        ...state,
        queryStatus: {
          attempt: false,
          success: false,
          error: action.payload,
        },
      };
    case QUERY_RESET:
      return {
        ...state,
        queryStatus: {
          attempt: false,
          success: false,
          error: null,
        },
      };
    case RESET_CONFIGURE_FORM:
      return {
        ...state,
        queryStatus: {
          attempt: false,
          success: false,
          error: null,
        },
        questions: [],
        pricing: null,
        variables: null,
        triggerQueryRequestAfterFormReset: true,
        resetDiscountLines: new Set<string>(),
      };
    case SET_TRIGGER_QUERY_REQUEST_AFTER_FORM_RESET: // Used in useValuesDidUpdate
      return {
        ...state,
        triggerQueryRequestAfterFormReset: action.payload,
      };
    case SET_MODELS:
      return {
        ...state,
        models: action.payload,
      };
    case SET_LANG:
      return {
        ...state,
        lang: action.payload,
      };
    case SET_IS_USING_SELLER_SYSTEM_DISCOUNTS:
      return {
        ...state,
        isUsingSupplierSystemDiscounts: action.payload,
      };
    case SET_EXTRA_PRICE_QUESTIONS:
      return {
        ...state,
        extraPriceQuestions: action.payload,
      };
    case SET_RESET_DISCOUNT_LINES:
      return {
        ...state,
        resetDiscountLines: action.payload,
      };
    case COMPANY_CHANGED:
      // A model belongs to the company it was loaded under, so drop all of it.
      // resetDiscountLines gets a fresh Set - INIT_STATE's instance is shared.
      return {
        ...INIT_STATE,
        resetDiscountLines: new Set<string>(),
      };
    case SET_PREDEFINED_PRICING:
      return {
        ...state,
        predefinedPricing: action.payload,
      };

    default:
      return state;
  }
};

export default modelReducer;
